import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Search, Filter, Plus, TrendingUp, Clock, CheckCircle, ListChecks, MessageSquare } from 'lucide-react';
import { PageOnboardingTooltip, OnboardingSlide } from '@/components/onboarding/PageOnboardingTooltip';

const dealProgressSlides: OnboardingSlide[] = [
  { icon: TrendingUp, secondaryIcons: [Clock], title: 'Track Your Deals', description: 'See all your active, pending, and completed deals at a glance. The summary cards show key metrics like total value and deal count.', gradient: 'from-primary/20 to-primary/5' },
  { icon: ListChecks, secondaryIcons: [CheckCircle], title: 'Milestones & Deliverables', description: 'Each deal tracks progress with milestones. Click any deal card to see detailed deliverables, timelines, and completion status.', gradient: 'from-blue-500/20 to-blue-500/5' },
  { icon: MessageSquare, secondaryIcons: [Filter], title: 'Search & Filter', description: 'Use the search bar and status filter to quickly find specific deals. Stay organized as your deal portfolio grows.', gradient: 'from-emerald-500/20 to-emerald-500/5' },
];
import { useDeals } from '@/hooks/useDeals';
import { DealCard } from './DealCard';
import { DealDetailModal } from './DealDetailModal';
import { useUser } from '@/contexts/UserContext';

interface DealProgressTabProps {
  selectedAthleteId?: string;
}

export const DealProgressTab: React.FC<DealProgressTabProps> = ({ selectedAthleteId }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  const { deals, loading, error, calculateDealProgress } = useDeals(selectedAthleteId);
  const { accountType } = useUser();

  // Filter deals based on search and status
  const filteredDeals = deals.filter(deal => {
    const matchesSearch = deal.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         deal.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || deal.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Calculate summary stats
  const totalDeals = deals.length;
  const activeDeals = deals.filter(d => d.status === 'active').length;
  const completedDeals = deals.filter(d => d.status === 'completed').length;
  const totalValue = deals.reduce((sum, deal) => sum + deal.deal_value, 0);

  const handleViewDetails = (deal: any) => {
    setSelectedDeal(deal);
    setIsModalOpen(true);
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
                <div className="h-8 bg-muted rounded w-1/2"></div>
              </CardContent>
            </Card>
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader>
                <div className="h-4 bg-muted rounded w-3/4"></div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="h-4 bg-muted rounded"></div>
                  <div className="h-4 bg-muted rounded w-2/3"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <p className="text-destructive">{error}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <PageOnboardingTooltip storageKey="deal_progress_tooltip_dismissed" slides={dealProgressSlides} dialogTitle="Deal Progress Walkthrough" />
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Deals</p>
                <p className="text-2xl font-bold">{totalDeals}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Active Deals</p>
                <p className="text-2xl font-bold">{activeDeals}</p>
              </div>
              <Clock className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Completed</p>
                <p className="text-2xl font-bold">{completedDeals}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Value</p>
                <p className="text-2xl font-bold">${totalValue.toLocaleString()}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <CardTitle>Deal Progress</CardTitle>
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search deals..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-full sm:w-64"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-40">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
              {accountType === 'agent' && (
                <Button className="w-full sm:w-auto">
                  <Plus className="h-4 w-4 mr-2" />
                  New Deal
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        
        <CardContent>
          {filteredDeals.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredDeals.map((deal) => (
                <DealCard
                  key={deal.id}
                  deal={deal}
                  progress={calculateDealProgress(deal)}
                  onViewDetails={handleViewDetails}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <TrendingUp className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No deals found</h3>
              <p className="text-muted-foreground mb-4">
                {deals.length === 0 
                  ? "Browse the marketplace to discover and apply for deals." 
                  : "No deals match your current filters."}
              </p>
              {deals.length === 0 && (
                <Button onClick={() => window.location.href = '/marketplace'}>
                  Browse Marketplace
                </Button>
              )}
              {accountType === 'agent' && deals.length === 0 && (
                <Button variant="outline" className="ml-2">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Your First Deal
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Deal Detail Modal */}
      <DealDetailModal
        deal={selectedDeal}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedDeal(null);
        }}
        progress={selectedDeal ? calculateDealProgress(selectedDeal) : 0}
      />
    </div>
  );
};